import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Grid from 'apollo-react/components/Grid';
import IconButton from 'apollo-react/components/IconButton';
import MenuItem from 'apollo-react/components/MenuItem';
import Select from 'apollo-react/components/Select';
import Table, { compareStrings } from 'apollo-react/components/Table';
import Tooltip from 'apollo-react/components/Tooltip';
import FolderOpenIcon from 'apollo-react-icons/FolderOpen';
import Plus from 'apollo-react-icons/Plus';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import ActionCell from './editor/controls/table/ActionCell';
import EditableCell from './editor/controls/table/EditableCell';
import EditableCheckboxCell from './editor/controls/table/EditableCheckboxCell';
import EditableSelectCell from './editor/controls/table/EditableSelectCell';
import { getListOptions } from '../../api/entity';
import {
  compareBooleans,
  createStringSearchFilter,
} from '../../components/SavedSearchesTable/utils';
import DropdownFilter from '../../components/TableFilters/DropdownFilter';
import TextFieldFilter from '../../components/TableFilters/TextInputFilter';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { equalityFn } from '../../form-designer/utils/form-manager.utils';
import { getUITA } from '../../utils/commonUtils';
import {
  addEntityField,
  deleteEntityField,
  editEntityField,
  setSelectedFieldSet,
  validateEntityDesigner,
} from '../actions/entity-designer.actions';
import {
  ENTITY_FIELD_STATUS_INWORKS,
  FIELDS_COMPUTED_FORMULA,
  FIELDS_FIELD_NAME,
  FIELDS_FIELD_TYPE,
  FIELDS_FIELD_TYPE_LABEL,
  FIELDS_IS_COMPUTED,
  FIELDS_IS_EVENT_ENABLED,
  FIELDS_IS_PART_OF_AI_SEARCH,
  FIELDS_IS_REQUIRED,
  FIELDS_IS_USABLE,
  FIELDS_IS_USER_KEY,
  FIELDS_LABEL,
  FIELDS_RESOLVER_TYPE,
  FIELDS_STATUS_LABEL,
  FIELDS_VALIDATE_AGAINST_LABEL,
  RESOLVER_TYPE_LISTS,
  TAB_RESOLVED_FIELDS_INDEX,
  TABLE_FIELDS_CONTROL_TYPE,
} from '../constants/entity-designer.constants';
import { EntityContext } from '../context';
import { getFields, getHasSpecializedRight } from '../selectors/entity-designer.selectors';
import { getDefaultTypeId, modifyFieldTypesListItems } from '../utils/entity-designer.utils';
import { customBooleanSearchFilter, customStringSearchFilter } from '../utils/table-filters.utils';

const useStyles = makeStyles({
  columnMultipleLineDetails: {
    display: 'flex',
    flexDirection: 'column',
  },
  booleanCell: {
    display: 'flex',
    justifyContent: 'center',
  },
  addButtonContainer: {
    textAlign: 'right',
    paddingTop: '8px !important',
    paddingRight: '15px !important',
  },
  validateAgainstWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  validateAgainstDropdownWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '95%',
  },
});

const ValidateAgainstCell = ({ row, column: { accessor: key } }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const handleClick = () => {
    dispatch(setSelectedFieldSet(row.rowId));
    row.navigateToTab(TAB_RESOLVED_FIELDS_INDEX);
  };

  const handleItemSelect = (value) => {
    if (row[key] === RESOLVER_TYPE_LISTS && value !== RESOLVER_TYPE_LISTS) {
      row.editRow({
        rowId: row.rowId,
        key: 'ResolverTypeList',
        value: '',
      });
      row.editRow({
        rowId: row.rowId,
        key: 'ResolverTypeListParent',
        value: '',
      });
    }

    row.editRow({ rowId: row.rowId, key, value });
    const entityCatalogItem = row.entityCatalogItems?.find((item) => item.value === value);

    row.editRow({
      rowId: row.rowId,
      key: 'ResolverTypeValueField',
      value: entityCatalogItem?.resolverTypeValue || null,
    });
  };

  if (row.FieldSet && row.ResolverFor) {
    return (
      <div className={classes.columnMultipleLineDetails}>
        <span>{`[${row.FieldSet}]`}</span>
        <span>{`${row.linkedField?.ResolverType}.${row.ResolverFor}`}</span>
      </div>
    );
  } else if (!row.FieldSet && !row.ResolverFor) {
    return row.isEditMode ? (
      <div className={classes.validateAgainstWrapper}>
        <div className={classes.validateAgainstDropdownWrapper}>
          <Select
            size="small"
            fullWidth
            value={row[key]}
            onChange={({ target: { value } }) => handleItemSelect(value)}
            disabled={row.hasResolvedFields || row.IsComputed}
          >
            {row.entityCatalogItems?.map((option, index) => (
              <MenuItem key={option.value + index} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {row[key] === RESOLVER_TYPE_LISTS && (
            <Select
              size="small"
              fullWidth
              value={row.ResolverTypeList}
              onChange={({ target: { value } }) =>
                row.editRow({
                  rowId: row.rowId,
                  key: 'ResolverTypeList',
                  value,
                })
              }
              disabled={row.hasResolvedFields || row.IsComputed}
            >
              {row.listOptions?.map((option) => (
                <MenuItem key={option.PKID} value={option.LIST_NAME}>
                  {option.LIST_NAME}
                </MenuItem>
              ))}
            </Select>
          )}
        </div>
        {row[key] && (
          <Tooltip title={translateString('View Resolved Fields')}>
            <IconButton style={{ marginTop: 10 }} onClick={handleClick}>
              <FolderOpenIcon size="small" />
            </IconButton>
          </Tooltip>
        )}
      </div>
    ) : row[key] !== RESOLVER_TYPE_LISTS ? (
      row[key]
    ) : (
      `${row[key]}.${row.ResolverTypeList}`
    );
  } else {
    return <span>{translateString('Assign Resolved Field')}</span>;
  }
};

const StatusCell = ({ row, column: { accessor: key } }) => {
  return <>{!row.isNew ? row[key] : ''}</>;
};

const compareValidateAgainst = (_, sortOrder) =>
  compareStrings(FIELDS_VALIDATE_AGAINST_LABEL, sortOrder);
const compareFieldType = (_, sortOrder) => compareStrings(FIELDS_FIELD_TYPE_LABEL, sortOrder);

const columns = [
  {
    header: 'Name',
    accessor: FIELDS_FIELD_NAME,
    sortFunction: compareStrings,
    customCell: EditableCell,
    filterFunction: createStringSearchFilter(FIELDS_FIELD_NAME),
    filterComponent: TextFieldFilter,
    width: 250,
  },
  {
    header: 'Label',
    accessor: FIELDS_LABEL,
    sortFunction: compareStrings,
    customCell: EditableCell,
    filterFunction: createStringSearchFilter(FIELDS_LABEL),
    filterComponent: TextFieldFilter,
    width: 280,
  },
  {
    header: 'Validate Against',
    accessor: FIELDS_RESOLVER_TYPE,
    sortFunction: compareValidateAgainst,
    customCell: ValidateAgainstCell,
    filterFunction: customStringSearchFilter(FIELDS_VALIDATE_AGAINST_LABEL),
    filterComponent: TextFieldFilter,
    width: 250,
  },
  {
    header: 'Field Type',
    accessor: FIELDS_FIELD_TYPE,
    sortFunction: compareFieldType,
    customCell: EditableSelectCell('fieldTypes'),
    filterFunction: customStringSearchFilter(FIELDS_FIELD_TYPE_LABEL),
    filterComponent: TextFieldFilter,
    width: 150,
  },
  {
    header: 'Enabled',
    accessor: FIELDS_IS_USABLE,
    sortFunction: compareBooleans,
    customCell: EditableCheckboxCell,
    filterFunction: customBooleanSearchFilter(FIELDS_IS_USABLE),
    filterComponent: DropdownFilter,
  },
  {
    header: 'User Key',
    accessor: FIELDS_IS_USER_KEY,
    sortFunction: compareBooleans,
    customCell: EditableCheckboxCell,
    filterFunction: customBooleanSearchFilter(FIELDS_IS_USER_KEY),
    filterComponent: DropdownFilter,
  },
  {
    header: 'Required',
    accessor: FIELDS_IS_REQUIRED,
    sortFunction: compareBooleans,
    customCell: EditableCheckboxCell,
    filterFunction: customBooleanSearchFilter(FIELDS_IS_REQUIRED),
    filterComponent: DropdownFilter,
  },
  {
    header: 'Event Enabled',
    accessor: FIELDS_IS_EVENT_ENABLED,
    sortFunction: compareBooleans,
    customCell: EditableCheckboxCell,
    filterFunction: customBooleanSearchFilter(FIELDS_IS_EVENT_ENABLED),
    filterComponent: DropdownFilter,
  },
  {
    header: 'Is Part Of AI Search',
    accessor: FIELDS_IS_PART_OF_AI_SEARCH,
    sortFunction: compareBooleans,
    customCell: EditableCheckboxCell,
    filterFunction: customBooleanSearchFilter(FIELDS_IS_PART_OF_AI_SEARCH),
    filterComponent: DropdownFilter,
  },
  {
    header: 'Computed Value',
    accessor: FIELDS_IS_COMPUTED,
    sortFunction: compareBooleans,
    customCell: EditableCheckboxCell,
    filterFunction: customBooleanSearchFilter(FIELDS_IS_COMPUTED),
    filterComponent: DropdownFilter,
  },
  {
    header: 'Computed Value Formula',
    accessor: FIELDS_COMPUTED_FORMULA,
    sortFunction: compareStrings,
    customCell: EditableCell,
    filterFunction: createStringSearchFilter(FIELDS_COMPUTED_FORMULA),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Status',
    accessor: FIELDS_STATUS_LABEL,
    sortFunction: compareStrings,
    customCell: StatusCell,
    filterFunction: createStringSearchFilter(FIELDS_STATUS_LABEL),
    filterComponent: TextFieldFilter,
  },
  {
    accessor: 'action',
    customCell: ActionCell,
    align: 'right',
    width: 55,
  },
];

export const fieldsValidationProps = [
  {
    prop: FIELDS_FIELD_NAME,
    errorLabel: 'Field Name',
    required: true,
    unique: true,
    validation: {
      expression: '^([A-Za-z_])+([A-Za-z0-9_])*$',
      message:
        'Field Name must be of the format (Field123 or Field_123 or Field or _Field or _Field123)',
    },
  },
  {
    prop: FIELDS_FIELD_TYPE,
    errorLabel: 'Field Type',
    label: 'Field Type',
    required: true,
  },
  {
    prop: FIELDS_COMPUTED_FORMULA,
    errorLabel: 'Computed Value Formula',
  },
];

const FieldsTable = ({ fieldTypes, entityCatalogItems, setSelectedTab }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();
  const isEditMode = useContext(EntityContext);
  const fields = useSelector((state) => getFields(state, { fieldTypes }), equalityFn);
  const hasSpecializedRight = useSelector(getHasSpecializedRight);
  const [listOptions, setListOptions] = useState([]);

  useEffect(() => {
    const fetchListOptions = async () => {
      if (fields.some((field) => field.ResolverType === RESOLVER_TYPE_LISTS)) {
        const { listOptions } = await getListOptions();
        setListOptions(listOptions);
      }
    };
    fetchListOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(fields)]);

  const addRow = () => {
    const newField = {
      rowId: uuidv4(),
      isNew: true,
      FieldName: '',
      Label: '',
      FieldSet: '',
      IsUsable: true,
      IsUserKey: false,
      IsRequired: false,
      IsComputed: false,
      IsEventEnabled: false,
      ComputedFormula: '',
      FormatType: '',
      ResolverFor: '',
      ResolverType: '',
      ResolverTypeValueField: null,
      Status: ENTITY_FIELD_STATUS_INWORKS,
      StatusLabel: '',
      TypeId: getDefaultTypeId(fieldTypes),
    };

    dispatch(addEntityField(newField));
  };

  const editRow = (params) => {
    dispatch(editEntityField(params));
    params.error && dispatch(validateEntityDesigner());
  };

  const deleteRow = (params) => {
    dispatch(deleteEntityField(params));
  };

  const navigateToTab = (tabIndex) => {
    setSelectedTab(tabIndex);
  };

  return (
    <Grid container spacing={1}>
      {isEditMode && (
        <Grid classes={{ item: classes.addButtonContainer }} item xs={12}>
          <span ui-ta={getUITA('Entity Fields', 'AddButton')}>
            <Button color="primary" variant="secondary" onClick={addRow}>
              <Plus fontSize="small" />
              &nbsp;
              {translateString('Add')}
            </Button>
          </span>
        </Grid>
      )}
      <Grid style={{ overflowX: 'auto' }} item xs={12}>
        <Table
          columns={columns}
          emptyProps={{
            text: translateString('No data to display'),
          }}
          rowId="rowId"
          rows={fields.map((field) => ({
            ...field,
            isEditMode,
            hasSpecializedRight,
            fieldTableControlType: TABLE_FIELDS_CONTROL_TYPE,
            fieldTypes: modifyFieldTypesListItems(fieldTypes),
            translatedFieldLabel: translateString(field.Label),
            entityCatalogItems,
            listOptions,
            rows: fields,
            translateString,
            navigateToTab,
            editRow,
            deleteRow,
          }))}
          rowsPerPageOptions={[20, 50, 100, translateString('All')]}
          defaultRowsPerPage={20}
          showFilterIcon
          tablePaginationProps={{
            labelDisplayedRows: ({ from, to, count }) =>
              `${from}-${to} ${translateString('of')} ${count}`,
            truncate: true,
            labelRowsPerPage: translateString('Rows'),
            pageText: translateString('Page'),
            ofText: translateString('of'),
          }}
        />
      </Grid>
    </Grid>
  );
};

export default FieldsTable;
